import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';
import { Dropdown } from 'primereact/dropdown';
import UserDropdown from './UserDropdown';
import RoleDropdown from './RoleDropdown';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const AdministrationDialog = ({
    visible,
    onHide,
    selectedAdmin,
    formData,
    setFormData,
    onSave,
    onDelete,
    userList,
    roleList,
    isLoadingUsers,
    isLoadingRoles,
    isSaving,
    isUpdating,
    isDeleting,
    canUpdateUserRole
}) => {
    const { th } = useLanguage();
    const isEditMode = !!selectedAdmin;
    const isLoading = isSaving || isUpdating || isDeleting;

    const footerContent = (
        <div className="flex justify-content-end gap-2">
            {isEditMode && (
                <Button
                    label={th('common.delete')}
                    icon="pi pi-trash"
                    severity="danger"
                    onClick={onDelete}
                    loading={isDeleting}
                    disabled={isLoading}
                />
            )}
            <Button
                label={th('common.cancel')}
                icon="pi pi-times"
                outlined
                onClick={onHide}
                disabled={isLoading}
            />
            <Button
                label={th('common.save')}
                icon="pi pi-check"
                onClick={onSave}
                loading={isSaving || isUpdating}
                disabled={isLoading}
            />
        </div>
    );

    return (
        <Dialog
            header={isEditMode ? th('systemAdministrator.editTitle') : th('systemAdministrator.newTitle')}
            visible={visible}
            style={{ width: '650px' }}
            modal
            footer={footerContent}
            onHide={onHide}
            closable={!isLoading}
        >
            <div className="flex flex-column gap-4 p-3">
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="user" className="font-bold block mb-2">
                                {th('systemAdministrator.user')} <span className="text-red-500">*</span>
                            </label>
                            <UserDropdown
                                value={formData.UserId}
                                onChange={(value) => setFormData({ ...formData, UserId: value })}
                                userList={userList}
                                isLoading={isLoadingUsers}
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="userType" className="font-bold block mb-2">
                                {th('systemAdministrator.userType')} <span className="text-red-500">*</span>
                            </label>
                            <RoleDropdown
                                value={formData.Authority}
                                onChange={(value) => setFormData({ ...formData, Authority: value })}
                                roleList={roleList}
                                isLoading={isLoadingRoles}
                                disabled={isLoading || !canUpdateUserRole}
                                placeholder={th('systemAdministrator.userTypePlaceholder')}
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="startDate" className="font-bold block mb-2">
                                {th('systemAdministrator.startDate')} <span className="text-red-500">*</span>
                            </label>
                            <Calendar
                                id="startDate"
                                value={formData.StartDate}
                                onChange={(e) => setFormData({ ...formData, StartDate: e.value })}
                                dateFormat="dd/mm/yy"
                                className="w-full"
                                disabled={isLoading}
                                showIcon
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label htmlFor="endDate" className="font-bold block mb-2">
                                {th('systemAdministrator.endDate')}
                            </label>
                            <Calendar
                                id="endDate"
                                value={formData.EndDate}
                                onChange={(e) => setFormData({ ...formData, EndDate: e.value })}
                                dateFormat="dd/mm/yy"
                                className="w-full"
                                disabled={isLoading}
                                showIcon
                                minDate={formData.StartDate}
                                placeholder={th('systemAdministrator.endDatePlaceholder')}
                            />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="field">
                            <label htmlFor="description" className="font-bold block mb-2">
                                {th('systemAdministrator.description')}
                            </label>
                            <InputTextarea
                                id="description"
                                value={formData.Description}
                                onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                                rows={4}
                                className="w-full"
                                disabled={isLoading}
                                maxLength={500}
                                placeholder={th('systemAdministrator.descriptionPlaceholder')}
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label className="font-bold block mb-2">{th('systemAdministrator.status')}</label>
                            <Dropdown
                                value={formData.IsActive}
                                options={[
                                    { label: th('systemAdministrator.active'), value: 1 },
                                    { label: th('systemAdministrator.inactive'), value: 0 }
                                ]}
                                onChange={(e) => setFormData({ ...formData, IsActive: e.value })}
                                placeholder={th('systemAdministrator.statusPlaceholder')}
                                className="w-full"
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label className="font-bold block mb-2">{th('systemAdministrator.mailSend')}</label>
                            <div className="flex gap-3">
                                <div className="flex align-items-center">
                                    <RadioButton
                                        inputId="mailSendActive"
                                        name="mailSend"
                                        value={1}
                                        onChange={(e) => setFormData({ ...formData, MailSend: e.value })}
                                        checked={formData.MailSend === 1}
                                        disabled={isLoading}
                                    />
                                    <label htmlFor="mailSendActive" className="ml-2">
                                        {th('systemAdministrator.active')}
                                    </label>
                                </div>
                                <div className="flex align-items-center">
                                    <RadioButton
                                        inputId="mailSendInactive"
                                        name="mailSend"
                                        value={0}
                                        onChange={(e) => setFormData({ ...formData, MailSend: e.value })}
                                        checked={formData.MailSend === 0}
                                        disabled={isLoading}
                                    />
                                    <label htmlFor="mailSendInactive" className="ml-2">
                                        {th('systemAdministrator.inactive')}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default AdministrationDialog;